import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MapGL, { Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import defaultLogo from '../../assets/img/logo.svg';
import { MAPBOX_TOKEN } from '../../config/mapbox';

const isValidCoordinate = (value) => {
    const num = Number(value);
    return !Number.isNaN(num) && Number.isFinite(num);
};

// Greedy proximity clustering. Returns an array of cluster objects.
// threshold: max distance in degrees to merge two items into the same cluster.
const buildClusters = (items, threshold, getLatitude, getLongitude, canCluster) => {
    const assigned = new Set();
    const clusters = [];

    for (let i = 0; i < items.length; i++) {
        if (assigned.has(i)) continue;

        const lat1 = Number(getLatitude(items[i]));
        const lng1 = Number(getLongitude(items[i]));
        const members = [items[i]];
        assigned.add(i);

        for (let j = i + 1; j < items.length; j++) {
            if (assigned.has(j)) continue;
            if (canCluster && !canCluster(items[i], items[j])) continue;
            const lat2 = Number(getLatitude(items[j]));
            const lng2 = Number(getLongitude(items[j]));
            const cosLat = Math.cos(((lat1 + lat2) / 2) * (Math.PI / 180));
            const dist = Math.sqrt((lat1 - lat2) ** 2 + ((lng1 - lng2) * cosLat) ** 2);
            if (dist < threshold) {
                members.push(items[j]);
                assigned.add(j);
            }
        }

        const avgLat = members.reduce((s, item) => s + Number(getLatitude(item)), 0) / members.length;
        const avgLng = members.reduce((s, item) => s + Number(getLongitude(item)), 0) / members.length;

        clusters.push({
            key: members.map((item) => item._id || `${getLatitude(item)}_${getLongitude(item)}`).join('|'),
            items: members,
            count: members.length,
            latitude: avgLat,
            longitude: avgLng,
            city: String(items[i]?.city || items[i]?.cityName || '').trim(),
            country: String(items[i]?.country || '').trim(),
        });
    }

    return clusters;
};

const DiscoveryMap = ({
    items = [],
    height = 220,
    mapStyle = 'mapbox://styles/mapbox/light-v10',
    initialCenter = null,
    flyToCenter = null,
    singleItemZoom = 10,
    compactSpreadZoom = false,
    groupClickZoom = 8,
    spreadZoomOffset = 0,
    clusterPixelRadius = 80,
    maxClusterZoom = null,
    fitBoundsPadding = 0,
    fitBoundsMaxZoom = null,
    markerScale = 1,
    getLatitude = (item) => item.latitude,
    getLongitude = (item) => item.longitude,
    getTitle = (item) => item.title,
    getSubtitle = null,
    getCompanyCount = null,
    getLogo = (item) => item.logo,
    getLink = (item) => `/discovery/company/${item._id}`,
    ctaLabel = 'Explore →',
    getSecondaryLink = null,
    secondaryCtaLabel = null,
    getTertiaryLink = null,
    tertiaryCtaLabel = null,
    markerClassName = '',
    canCluster = null,
    groupBy = null,
    onClusterClick = null,
    onZoomEnd = null,
    onGroupClick = null,
    onLinkClick = null,
}) => {
    const validItems = useMemo(() => {
        return items.filter((item) => {
            const lat = Number(getLatitude(item));
            const lng = Number(getLongitude(item));
            return isValidCoordinate(lat) && isValidCoordinate(lng);
        });
    }, [items, getLatitude, getLongitude]);

    const initialViewport = useMemo(() => {
        if (initialCenter) return initialCenter;

        if (!validItems.length) {
            return { latitude: 50, longitude: 10, zoom: 3.4 };
        }

        if (validItems.length === 1) {
            return {
                latitude: Number(getLatitude(validItems[0])),
                longitude: Number(getLongitude(validItems[0])),
                zoom: singleItemZoom,
            };
        }

        const latitudes = validItems.map((item) => Number(getLatitude(item)));
        const longitudes = validItems.map((item) => Number(getLongitude(item)));

        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLng = Math.min(...longitudes);
        const maxLng = Math.max(...longitudes);

        const centerLat = latitudes.reduce((s, v) => s + v, 0) / latitudes.length;
        const centerLng = longitudes.reduce((s, v) => s + v, 0) / longitudes.length;

        const maxDiff = Math.max(maxLat - minLat, maxLng - minLng);

        let zoom = 4;
        if (compactSpreadZoom) {
            if (maxDiff < 0.05) zoom = 8.5;
            else if (maxDiff < 0.1) zoom = 7.5;
            else if (maxDiff < 0.5) zoom = 6.5;
            else if (maxDiff < 1) zoom = 6;
            else if (maxDiff < 5) zoom = 5;
            else zoom = 3.5;
        } else {
            if (maxDiff < 0.05) zoom = 11;
            else if (maxDiff < 0.1) zoom = 10;
            else if (maxDiff < 0.5) zoom = 8;
            else if (maxDiff < 1) zoom = 7;
            else if (maxDiff < 5) zoom = 5;
            else zoom = 3.5;
        }

        zoom += spreadZoomOffset;

        return { latitude: centerLat, longitude: centerLng, zoom };
    }, [initialCenter, singleItemZoom, compactSpreadZoom, spreadZoomOffset, validItems, getLatitude, getLongitude]);

    const boundsForFit = useMemo(() => {
        if (!validItems.length || fitBoundsPadding === 0) return null;
        // A single point only fits safely when a max zoom caps the degenerate bounds.
        if (validItems.length === 1 && fitBoundsMaxZoom == null) return null;
        const latitudes = validItems.map((item) => Number(getLatitude(item)));
        const longitudes = validItems.map((item) => Number(getLongitude(item)));
        return [
            [Math.min(...longitudes), Math.min(...latitudes)],
            [Math.max(...longitudes), Math.max(...latitudes)],
        ];
    }, [initialCenter, validItems, getLatitude, getLongitude, fitBoundsPadding, fitBoundsMaxZoom]);

    const handleMapLoad = useCallback(() => {
        if (!boundsForFit) return;
        mapRef.current?.getMap().fitBounds(boundsForFit, {
            padding: fitBoundsPadding,
            duration: 0,
            ...(fitBoundsMaxZoom != null ? { maxZoom: fitBoundsMaxZoom } : {}),
        });
    }, [boundsForFit, fitBoundsPadding, fitBoundsMaxZoom]);

    const mapRef = useRef(null);
    const [viewState, setViewState] = useState(initialViewport);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        setViewState(initialViewport);
    }, [initialViewport]);

    useEffect(() => {
        if (!flyToCenter || !mapRef.current) return;
        mapRef.current.flyTo({
            center: [flyToCenter.longitude, flyToCenter.latitude],
            zoom: flyToCenter.zoom ?? 10,
            duration: 700,
            essential: true,
        });
    }, [flyToCenter]);

    const currentZoom = Number(viewState?.zoom || initialViewport?.zoom || 4);

    // Discretise zoom so clustering only recalculates when zoom crosses a half-integer boundary.
    const clusterZoom = Math.round(currentZoom);

    const clusters = useMemo(() => {
        // Exact-key aggregation (e.g. one bubble per country/city) instead of
        // zoom-dependent proximity clustering. Items without a key are left out.
        if (groupBy) {
            const groups = new Map();
            for (const item of validItems) {
                const key = String(groupBy(item) || '').trim();
                if (!key) continue;
                const members = groups.get(key);
                if (members) members.push(item);
                else groups.set(key, [item]);
            }
            return Array.from(groups, ([key, members]) => ({
                key: `group_${key}`,
                groupKey: key,
                items: members,
                count: members.length,
                latitude: members.reduce((s, item) => s + Number(getLatitude(item)), 0) / members.length,
                longitude: members.reduce((s, item) => s + Number(getLongitude(item)), 0) / members.length,
            }));
        }
        if (maxClusterZoom != null && clusterZoom >= maxClusterZoom) {
            return validItems.map((item) => ({
                key: String(item._id || `${getLatitude(item)}_${getLongitude(item)}`),
                items: [item],
                count: 1,
                latitude: Number(getLatitude(item)),
                longitude: Number(getLongitude(item)),
            }));
        }
        // threshold in degrees ≈ clusterPixelRadius px worth of space at current zoom
        const threshold = (clusterPixelRadius * 360) / (256 * Math.pow(2, Math.max(clusterZoom, 1)));
        return buildClusters(validItems, threshold, getLatitude, getLongitude, canCluster);
    }, [groupBy, validItems, clusterZoom, clusterPixelRadius, maxClusterZoom, getLatitude, getLongitude, canCluster]);

    // Leaving an aggregation level shouldn't strand an open popup.
    useEffect(() => {
        if (groupBy) setSelectedItem(null);
    }, [groupBy]);

    const markerZoomClass =
        currentZoom <= 5
            ? 'is-zoomed-out'
            : currentZoom <= 7
                ? 'is-mid-zoom'
                : 'is-zoomed-in';

    return (
        <div className="discovery-map-wrapper" style={{ height: `${height}px`, overflow: 'hidden' }}>
            <MapGL
                {...viewState}
                ref={mapRef}
                mapStyle={mapStyle}
                mapboxAccessToken={MAPBOX_TOKEN}
                onMove={(evt) => setViewState(evt.viewState)}
                onMoveEnd={onZoomEnd
                    ? (evt) => onZoomEnd({
                        zoom: Number(evt.viewState?.zoom),
                        // Programmatic camera moves (flyTo/fitBounds) carry no originalEvent.
                        isUserInteraction: Boolean(evt.originalEvent),
                    })
                    : undefined}
                onLoad={handleMapLoad}
                attributionControl={false}
            >
                {clusters.map((cluster) => {
                    const isAggregate = Boolean(groupBy);
                    const isGroup = isAggregate || cluster.count > 1;

                    return (
                        <Marker
                            key={cluster.key}
                            latitude={cluster.latitude}
                            longitude={cluster.longitude}
                            anchor="bottom"
                            onClick={(e) => {
                                e.originalEvent.stopPropagation();

                                if (isAggregate) {
                                    setSelectedItem(null);
                                    if (mapRef.current) {
                                        const lngs = cluster.items.map((item) => Number(getLongitude(item)));
                                        const lats = cluster.items.map((item) => Number(getLatitude(item)));
                                        const minLng = Math.min(...lngs);
                                        const maxLng = Math.max(...lngs);
                                        const minLat = Math.min(...lats);
                                        const maxLat = Math.max(...lats);
                                        if (maxLng - minLng < 0.01 && maxLat - minLat < 0.01) {
                                            mapRef.current.flyTo({
                                                center: [cluster.longitude, cluster.latitude],
                                                zoom: Math.max(currentZoom + 2, groupClickZoom),
                                                duration: 600,
                                                essential: true,
                                            });
                                        } else {
                                            mapRef.current.fitBounds(
                                                [[minLng, minLat], [maxLng, maxLat]],
                                                { padding: 60, duration: 600, essential: true, maxZoom: groupClickZoom }
                                            );
                                        }
                                    }
                                    if (onClusterClick) onClusterClick(cluster);
                                    return;
                                }

                                if (!isGroup) {
                                    if (onGroupClick) {
                                        onGroupClick(cluster);
                                        return;
                                    }
                                    const lng = Number(getLongitude(cluster.items[0]));
                                    const lat = Number(getLatitude(cluster.items[0]));
                                    setSelectedItem(cluster.items[0]);
                                    if (mapRef.current) {
                                        // Popup always opens above — pan marker to 75% of viewport
                                        // height so the popup has room above it.
                                        mapRef.current.easeTo({
                                            center: [lng, lat],
                                            offset: [0, height * 0.25],
                                            duration: 300,
                                        });
                                    }
                                    return;
                                }

                                setSelectedItem(null);
                                if (mapRef.current) {
                                    const lngs = cluster.items.map((item) => Number(getLongitude(item)));
                                    const lats = cluster.items.map((item) => Number(getLatitude(item)));
                                    const minLng = Math.min(...lngs);
                                    const maxLng = Math.max(...lngs);
                                    const minLat = Math.min(...lats);
                                    const maxLat = Math.max(...lats);
                                    if (maxLng - minLng < 0.01 && maxLat - minLat < 0.01) {
                                        mapRef.current.flyTo({
                                            center: [cluster.longitude, cluster.latitude],
                                            zoom: Math.max(currentZoom + 2, (maxClusterZoom ?? 0) + 1),
                                            duration: 600,
                                            essential: true,
                                        });
                                    } else {
                                        const clusterCenterLng = (minLng + maxLng) / 2;
                                        const clusterCenterLat = (minLat + maxLat) / 2;
                                        mapRef.current.fitBounds(
                                            [[minLng, minLat], [maxLng, maxLat]],
                                            {
                                                padding: 60,
                                                duration: 600,
                                                essential: true,
                                                maxZoom: groupClickZoom,
                                            }
                                        );
                                        if (maxClusterZoom != null) {
                                            const mapInstance = mapRef.current.getMap();
                                            const onMoveEnd = () => {
                                                mapInstance.off('moveend', onMoveEnd);
                                                if (mapInstance.getZoom() < maxClusterZoom) {
                                                    mapInstance.flyTo({
                                                        center: [clusterCenterLng, clusterCenterLat],
                                                        zoom: maxClusterZoom,
                                                        duration: 400,
                                                    });
                                                }
                                            };
                                            mapInstance.on('moveend', onMoveEnd);
                                        }
                                    }
                                } else {
                                    setViewState((prev) => ({
                                        ...prev,
                                        latitude: cluster.latitude,
                                        longitude: cluster.longitude,
                                        zoom: currentZoom + 2,
                                    }));
                                }
                            }}
                        >
                            {isGroup ? (
                                <button
                                    type="button"
                                    className={`discovery-map-marker discovery-map-marker--group ${markerZoomClass}`}
                                    style={markerScale !== 1 ? { transform: `scale(${markerScale})` } : undefined}
                                    aria-label={isAggregate && cluster.groupKey ? `${cluster.groupKey} (${cluster.count})` : `Group (${cluster.count})`}
                                >
                                    <span>{cluster.count}</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={`discovery-map-marker ${markerZoomClass}${markerClassName ? ` ${markerClassName}` : ''}`}
                                    style={markerScale !== 1 ? { transform: `scale(${markerScale})` } : undefined}
                                    aria-label={getTitle(cluster.items[0])}
                                >
                                    <span className="discovery-map-marker__logo-wrap">
                                        <img
                                            src={getLogo(cluster.items[0]) || defaultLogo}
                                            alt={getTitle(cluster.items[0])}
                                            className="discovery-map-marker__logo"
                                            onError={(e) => { e.currentTarget.src = defaultLogo; }}
                                        />
                                    </span>
                                </button>
                            )}
                        </Marker>
                    );
                })}

                {selectedItem && (
                    <Popup
                        anchor="bottom"
                        latitude={Number(getLatitude(selectedItem))}
                        longitude={Number(getLongitude(selectedItem))}
                        onClose={() => setSelectedItem(null)}
                        closeButton={true}
                        closeOnClick={false}
                        offset={20}
                        maxWidth="230px"
                    >
                        <div className="discovery-map-popup">
                            <p className="discovery-map-popup__city">{getTitle(selectedItem)}</p>
                            {getSubtitle && getSubtitle(selectedItem) && (
                                <p className="discovery-map-popup__country">{getSubtitle(selectedItem)}</p>
                            )}
                            {getCompanyCount && getCompanyCount(selectedItem) != null && (
                                <p className="discovery-map-popup__meta">
                                    {getCompanyCount(selectedItem)}{' '}
                                    {getCompanyCount(selectedItem) === 1 ? 'company available' : 'companies available'}
                                </p>
                            )}
                            <hr className="discovery-map-popup__divider" />
                            <div className="discovery-map-popup__actions">
                                <Link
                                    to={getLink(selectedItem)}
                                    className="discovery-map-popup__cta"
                                    onClick={() => onLinkClick && onLinkClick(selectedItem)}
                                >
                                    {ctaLabel}
                                </Link>
                                {getSecondaryLink && secondaryCtaLabel && (
                                    <Link to={getSecondaryLink(selectedItem)} className="discovery-map-popup__cta discovery-map-popup__cta--secondary">
                                        {secondaryCtaLabel}
                                    </Link>
                                )}
                                {getTertiaryLink && tertiaryCtaLabel && (
                                    <Link to={getTertiaryLink(selectedItem)} className="discovery-map-popup__cta discovery-map-popup__cta--secondary">
                                        {tertiaryCtaLabel}
                                    </Link>
                                )}
                            </div>
                        </div>
                    </Popup>
                )}
            </MapGL>
        </div>
    );
};

export default DiscoveryMap;
